import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Tag, 
  ChevronRight, 
  X, 
  Filter, 
  Ticket, 
  ArrowRight, 
  Search,
  Image as ImageIcon,
  ChevronLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';

const EventkalenderOgGalleri = () => {
  const [activeTab, setActiveTab] = useState('events'); // 'events' or 'gallery'
  
  // Events state
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [selectedEventCategory, setSelectedEventCategory] = useState('alle');
  const [eventSearchQuery, setEventSearchQuery] = useState('');

  // Gallery state
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [selectedGalleryCategory, setSelectedGalleryCategory] = useState('alle');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Event categories (from taxonomy)
  const [eventCategories, setEventCategories] = useState([
    { slug: 'alle', label: 'Alle begivenheder' },
    { slug: 'koncert', label: 'Koncerter' },
    { slug: 'gastronomi', label: 'Gastronomi' },
    { slug: 'foredrag', label: 'Foredrag' },
    { slug: 'saesonbegivenhed', label: 'Sæson' }
  ]);

  // Gallery categories (from taxonomy)
  const [galleryCategories, setGalleryCategories] = useState([
    { slug: 'alle', label: 'Alle billeder' },
    { slug: 'bryllup', label: 'Bryllup' },
    { slug: 'fest', label: 'Fest & Selskab' },
    { slug: 'konference', label: 'Konference' },
    { slug: 'omgivelser', label: 'Omgivelser' }
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch categories dynamically from CMS taxonomies
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const eventRes = await fetch('https://api.dev.auxo.dk/public/taxonomies/event-category/terms');
        if (eventRes.ok) {
          const terms = await eventRes.json();
          if (Array.isArray(terms)) {
            setEventCategories([
              { slug: 'alle', label: 'Alle begivenheder' },
              ...terms.map(t => ({ slug: t.slug, label: t.label }))
            ]);
          }
        }
      } catch (err) {
        console.error('Error fetching event categories:', err);
      }

      try {
        const galleryRes = await fetch('https://api.dev.auxo.dk/public/taxonomies/gallerycat/terms');
        if (galleryRes.ok) {
          const terms = await galleryRes.json();
          if (Array.isArray(terms)) {
            setGalleryCategories([
              { slug: 'alle', label: 'Alle billeder' },
              ...terms.map(t => ({ slug: t.slug, label: t.label }))
            ]);
          }
        }
      } catch (err) {
        console.error('Error fetching gallery categories:', err);
      }
    };

    fetchCategories();
  }, []);

  // Fetch Events
  useEffect(() => {
    const fetchEvents = async () => {
      setEventsLoading(true);
      try {
        let url = 'https://api.dev.auxo.dk/public/v2/content/events?sort=date&order=asc';
        if (selectedEventCategory !== 'alle') {
          url += `&term=event-category:${selectedEventCategory}`;
        }
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setEvents(data.items || []);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setEventsLoading(false);
      }
    };

    fetchEvents();
  }, [selectedEventCategory]);

  // Fetch Gallery
  useEffect(() => {
    const fetchGallery = async () => {
      setGalleryLoading(true);
      try {
        let url = 'https://api.dev.auxo.dk/public/v2/content/gallery';
        if (selectedGalleryCategory !== 'alle') {
          url += `?term=gallerycat:${selectedGalleryCategory}`;
        }
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setGalleryItems(data.items || []);
        }
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setGalleryLoading(false);
      }
    };

    fetchGallery();
  }, [selectedGalleryCategory]);

  // Filter events by search query locally on top of server-side category filter
  const filteredEvents = events.filter(event => {
    const title = event.title?.da || '';
    const desc = event.description?.da || '';
    const loc = event.location?.da || '';
    const query = eventSearchQuery.toLowerCase();
    return title.toLowerCase().includes(query) || 
           desc.toLowerCase().includes(query) || 
           loc.toLowerCase().includes(query);
  });

  // Helper to get category label from slug
  const getEventCategoryLabel = (slug) => {
    if (!slug) return 'Begivenhed';
    const cat = eventCategories.find(c => c.slug === slug);
    return cat ? cat.label : slug.charAt(0).toUpperCase() + slug.slice(1);
  };

  const getGalleryCategoryLabel = (slug) => {
    if (!slug) return 'Billede';
    const cat = galleryCategories.find(c => c.slug === slug);
    return cat ? cat.label : slug.charAt(0).toUpperCase() + slug.slice(1);
  };

  // Format date to Danish readable format (e.g. "18. sep")
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = date.getDate();
    const months = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
    const month = months[date.getMonth()];
    return { day, month, year: date.getFullYear() };
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev === 0 ? galleryItems.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev === galleryItems.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="pt-0 bg-stone-50 min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <img 
          src="/Kundebilleder/imgi_25_1000001150-scaled.jpg" 
          alt="GuldBoSund Omgivelser" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 text-center px-4 mt-20 max-w-4xl mx-auto">
          <span className="text-[#d6b14e] uppercase tracking-[0.3em] text-xs font-semibold mb-3 block">
            Oplev GuldBoSund
          </span>
          <h1 className="text-4xl md:text-6xl font-light text-white uppercase tracking-[0.15em] mb-4">
            Eventkalender & Galleri
          </h1>
          <p className="text-white/80 text-base md:text-lg font-light tracking-wide max-w-2xl mx-auto">
            Gå på opdagelse i vores kommende arrangementer eller lad dig inspirere af billeder fra vores smukke herregård og naturskønne omgivelser.
          </p>
        </div>
      </section>

      {/* Tab Switcher */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20">
        <div className="bg-[#081f32] p-2 rounded-2xl shadow-xl flex max-w-md mx-auto border border-white/10">
          <button
            onClick={() => setActiveTab('events')}
            className={`flex-1 py-3 px-4 rounded-xl text-xs uppercase tracking-widest font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'events'
                ? 'bg-[#d6b14e] text-[#081f32]'
                : 'text-white/70 hover:text-white hover:bg-white/5'
            }`}
          >
            <Calendar size={14} />
            Eventkalender
          </button>
          <button
            onClick={() => setActiveTab('gallery')}
            className={`flex-1 py-3 px-4 rounded-xl text-xs uppercase tracking-widest font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'gallery'
                ? 'bg-[#d6b14e] text-[#081f32]'
                : 'text-white/70 hover:text-white hover:bg-white/5'
            }`}
          >
            <ImageIcon size={14} />
            Billedgalleri
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <AnimatePresence mode="wait">
          {activeTab === 'events' ? (
            <motion.div
              key="events-tab"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              {/* Events Header & Filter Controls */}
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-12 pb-8 border-b border-stone-200">
                <div>
                  <h2 className="text-2xl md:text-3xl font-light text-[#081f32] uppercase tracking-wider mb-2">
                    Kommende Arrangementer
                  </h2>
                  <p className="text-stone-500 font-light text-sm">
                    Oplev koncerter, foredrag, gastronomiske aftener og meget mere ved Guldborgsund.
                  </p>
                </div>

                {/* Search Bar */}
                <div className="relative w-full lg:w-80">
                  <input
                    type="text"
                    placeholder="Søg i begivenheder..."
                    value={eventSearchQuery}
                    onChange={(e) => setEventSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-stone-200 bg-white text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-[#d6b14e] focus:border-transparent text-sm transition-all"
                  />
                  <Search className="absolute left-3.5 top-3 text-stone-400" size={16} />
                  {eventSearchQuery && (
                    <button 
                      onClick={() => setEventSearchQuery('')}
                      className="absolute right-3 top-3 text-stone-400 hover:text-stone-600"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* Category Filter Chips */}
              <div className="flex flex-wrap gap-2 mb-10 overflow-x-auto pb-2">
                {eventCategories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => setSelectedEventCategory(cat.slug)}
                    className={`px-5 py-2.5 rounded-full text-xs uppercase tracking-wider transition-all whitespace-nowrap border ${
                      selectedEventCategory === cat.slug
                        ? 'bg-[#081f32] text-white border-[#081f32] shadow-md'
                        : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              {/* Events Grid */}
              {eventsLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d6b14e]"></div>
                  <p className="text-stone-500 mt-4 font-light text-sm">Henter begivenheder...</p>
                </div>
              ) : filteredEvents.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {filteredEvents.map((event) => {
                    const dateInfo = formatDate(event.date);
                    return (
                      <motion.div
                        key={event.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-stone-100 flex flex-col md:flex-row group"
                      >
                        {/* Image Section */}
                        <div className="relative w-full md:w-2/5 h-64 md:h-auto overflow-hidden">
                          <img
                            src={event.image?.[0] || '/Kundebilleder/imgi_25_1000001150-scaled.jpg'}
                            alt={event.title?.da}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent md:hidden" />
                          
                          {/* Date Badge (Floating on image) */}
                          <div className="absolute top-4 left-4 bg-[#081f32] text-white p-3 rounded-2xl text-center min-w-[65px] shadow-lg border border-white/10">
                            <span className="block text-xl font-semibold leading-none">{dateInfo.day}</span>
                            <span className="block text-[10px] uppercase tracking-widest font-light mt-1 text-[#d6b14e]">{dateInfo.month}</span>
                            <span className="block text-[9px] text-white/50 font-light">{dateInfo.year}</span>
                          </div>
                        </div>

                        {/* Content Section */}
                        <div className="p-6 md:p-8 flex-1 flex flex-col justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <span className="bg-[#d6b14e]/10 text-[#081f32] px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-medium">
                                {getEventCategoryLabel(event.terms?.['event-category']?.[0])}
                              </span>
                            </div>
                            
                            <h3 className="text-xl font-light text-[#081f32] uppercase tracking-wide mb-4 leading-snug">
                              {event.title?.da}
                            </h3>

                            {/* Event details */}
                            <div className="space-y-2.5 mb-6 text-stone-600 text-sm font-light">
                              <div className="flex items-center gap-3">
                                <Clock size={16} className="text-[#d6b14e]" />
                                <span>Kl. {event.time}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <MapPin size={16} className="text-[#d6b14e]" />
                                <span>{event.location?.da}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <Ticket size={16} className="text-[#d6b14e]" />
                                <span>{event.price?.da}</span>
                              </div>
                            </div>

                            {/* Description */}
                            <div 
                              className="text-stone-500 font-light text-sm leading-relaxed mb-6 line-clamp-3"
                              dangerouslySetInnerHTML={{ __html: event.description?.da }}
                            />
                          </div>

                          {/* CTA Button */}
                          <Link
                            to={event.ticket_link || '/kontakt'}
                            className="inline-flex items-center justify-center gap-2 bg-[#081f32] hover:bg-[#d6b14e] text-white hover:text-[#081f32] px-6 py-3 rounded-xl text-xs uppercase tracking-widest font-light transition-all w-full text-center"
                          >
                            Tilmeld / Book Billet
                            <ArrowRight size={14} />
                          </Link>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-20 bg-white rounded-3xl border border-stone-100 shadow-sm">
                  <Calendar className="mx-auto text-stone-300 mb-4" size={48} />
                  <h3 className="text-lg font-light text-[#081f32] uppercase tracking-wider mb-2">Ingen begivenheder fundet</h3>
                  <p className="text-stone-500 font-light text-sm max-w-md mx-auto">
                    Vi har i øjeblikket ingen kommende begivenheder i denne kategori. Prøv at vælge en anden kategori eller søg efter noget andet.
                  </p>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="gallery-tab"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              {/* Gallery Header & Filter Controls */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12 pb-8 border-b border-stone-200">
                <div>
                  <h2 className="text-2xl md:text-3xl font-light text-[#081f32] uppercase tracking-wider mb-2">
                    Billedgalleri
                  </h2>
                  <p className="text-stone-500 font-light text-sm">
                    Gå på opdagelse i stemningsfulde billeder fra vores herregård, fester, bryllupper og konferencer.
                  </p>
                </div>
              </div>

              {/* Category Filter Chips */}
              <div className="flex flex-wrap gap-2 mb-10 overflow-x-auto pb-2">
                {galleryCategories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => setSelectedGalleryCategory(cat.slug)}
                    className={`px-5 py-2.5 rounded-full text-xs uppercase tracking-wider transition-all whitespace-nowrap border ${
                      selectedGalleryCategory === cat.slug
                        ? 'bg-[#081f32] text-white border-[#081f32] shadow-md'
                        : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              {/* Gallery Grid */}
              {galleryLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d6b14e]"></div>
                  <p className="text-stone-500 mt-4 font-light text-sm">Henter galleribilleder...</p>
                </div>
              ) : galleryItems.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {galleryItems.map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                      onClick={() => setLightboxIndex(index)}
                      className="group relative h-80 rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border border-stone-100"
                    >
                      <img
                        src={item.image?.[0] || '/Kundebilleder/imgi_25_1000001150-scaled.jpg'}
                        alt={item.title?.da}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-6" />
                      
                      {/* Floating Title (Always visible on mobile, hover on desktop) */}
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                        <span className="text-[#d6b14e] text-[10px] uppercase tracking-widest font-medium mb-1.5 block">
                          {getGalleryCategoryLabel(item.terms?.['gallerycat']?.[0])}
                        </span>
                        <h3 className="text-lg font-light uppercase tracking-wide leading-snug">
                          {item.title?.da}
                        </h3>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 bg-white rounded-3xl border border-stone-100 shadow-sm">
                  <ImageIcon className="mx-auto text-stone-300 mb-4" size={48} />
                  <h3 className="text-lg font-light text-[#081f32] uppercase tracking-wider mb-2">Ingen billeder fundet</h3>
                  <p className="text-stone-500 font-light text-sm max-w-md mx-auto">
                    Vi har i øjeblikket ingen billeder i denne kategori. Prøv at vælge en anden kategori.
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {lightboxIndex !== null && galleryItems[lightboxIndex] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightboxIndex(null)}
            className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center p-4 md:p-8"
          >
            {/* Close Button */}
            <button
              onClick={() => setLightboxIndex(null)}
              className="absolute top-6 right-6 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-3 rounded-full transition-all"
            >
              <X size={24} />
            </button>

            {/* Navigation Buttons */}
            <button
              onClick={handlePrevImage}
              className="absolute left-4 md:left-8 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-3 rounded-full transition-all"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-4 md:right-8 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-3 rounded-full transition-all"
            >
              <ChevronRight size={24} />
            </button>

            {/* Image Container */}
            <div className="max-w-5xl max-h-[75vh] w-full h-full flex items-center justify-center relative">
              <motion.img
                key={lightboxIndex}
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ duration: 0.3 }}
                src={galleryItems[lightboxIndex].image?.[0]}
                alt={galleryItems[lightboxIndex].title?.da}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Caption */}
            <div className="text-center mt-6 max-w-2xl" onClick={(e) => e.stopPropagation()}>
              <span className="text-[#d6b14e] text-xs uppercase tracking-[0.2em] font-medium mb-1 block">
                {getGalleryCategoryLabel(galleryItems[lightboxIndex].terms?.['gallerycat']?.[0])}
              </span>
              <h3 className="text-white text-xl font-light uppercase tracking-wider">
                {galleryItems[lightboxIndex].title?.da}
              </h3>
              <p className="text-white/40 text-xs mt-2">
                Billede {lightboxIndex + 1} af {galleryItems.length}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EventkalenderOgGalleri;
